import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Square, Pencil, Mic, Send, Wrench, ChevronRight } from 'lucide-react';
import { CodeMessage, CodeSession, FinalActionChoice, ToolCall } from './types';
interface Props {
  session: CodeSession;
  onInterrupt: () => void;
  onSendMessage: (content: string) => void;
  onPickChoice: (choice: FinalActionChoice) => void;
}
export function SessionTab({
  session,
  onInterrupt,
  onSendMessage,
  onPickChoice
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [inputMode, setInputMode] = useState<'idle' | 'text' | 'voice'>('idle');
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session.messages.length, session.status]);
  const isRunning = session.status === 'running';
  const handleSend = () => {
    if (!draft.trim()) return;
    onSendMessage(draft.trim());
    setDraft('');
    setInputMode('idle');
  };
  return <div className="flex flex-col h-full min-h-0" data-id="element-563">
      {/* Scrollable conversation */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-id="element-564">
        {session.messages.map(msg => <MessageBubble key={msg.id} message={msg} data-id="element-565" />)}

        {/* Final action card — inline as last element */}
        {session.status === 'waiting' && session.finalAction && <FinalActionCard question={session.finalAction.question} kind={session.finalAction.kind} choices={session.finalAction.choices} onPickChoice={onPickChoice} data-id="element-566" />}

        {isRunning && <div className="flex items-center gap-2 px-3 py-2" data-id="element-567">
            <div className="flex gap-1" data-id="element-568">
              <motion.div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a]" animate={{
            opacity: [0.3, 1, 0.3]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: 0
          }} data-id="element-569" />
              <motion.div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a]" animate={{
            opacity: [0.3, 1, 0.3]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: 0.2
          }} data-id="element-570" />
              <motion.div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a]" animate={{
            opacity: [0.3, 1, 0.3]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: 0.4
          }} data-id="element-571" />
            </div>
            <span className="text-[12px] text-[#8a8680]" data-id="element-572">Agent working…</span>
          </div>}
      </div>

      {/* Bottom bar — state-aware */}
      <div className="flex-shrink-0 px-4 pt-2 pb-5 bg-[#141418]/80 backdrop-blur border-t border-white/[0.04]" data-id="element-573">
        <AnimatePresence mode="wait" data-id="element-574">
          {isRunning && inputMode === 'idle' ? <motion.button key="interrupt" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 10
        }} onClick={onInterrupt} className="w-full h-12 flex items-center justify-center gap-2 rounded-full bg-[#3a1a1a] border border-[#ff6b6b]/40 text-[#ff9a9a] hover:bg-[#4a1a1a] transition-colors" data-id="element-575">
              <Square size={13} strokeWidth={2} data-id="element-576" />
              <span className="text-[13px]" data-id="element-577">Interrupt</span>
            </motion.button> : inputMode === 'text' ? <motion.div key="text" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 10
        }} className="flex items-center gap-2 bg-[#1c1c22] border border-white/[0.06] rounded-full px-4 h-12" data-id="element-578">
              <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
            if (e.key === 'Escape') setInputMode('idle');
          }} placeholder="Send a message…" className="flex-1 bg-transparent text-[14px] text-[#e8e4df] placeholder:text-[#8a8680]/60 outline-none" data-id="element-579" />
              <button onClick={handleSend} disabled={!draft.trim()} className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4a9a9a] text-white disabled:opacity-30 hover:bg-[#5ab0b0] transition-colors" data-id="element-580">
                <Send size={13} strokeWidth={2} data-id="element-581" />
              </button>
            </motion.div> : inputMode === 'voice' ? <motion.div key="voice" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 10
        }} className="flex items-center gap-3 bg-[#1c1c22] border border-white/[0.06] rounded-full px-5 h-12" data-id="element-582">
              <motion.div animate={{
            scale: [1, 1.3, 1],
            opacity: [0.6, 1, 0.6]
          }} transition={{
            duration: 1.2,
            repeat: Infinity
          }} className="w-3 h-3 rounded-full bg-[#4a9a9a]" data-id="element-583" />
              <span className="flex-1 text-[13px] text-[#e8e4df]" data-id="element-584">Listening…</span>
              <button onClick={() => {
            onSendMessage('(voice reply) That sounds good — go with option A.');
            setInputMode('idle');
          }} className="text-[12px] text-[#4a9a9a] hover:text-[#5ab0b0]" data-id="element-585">
                Stop
              </button>
            </motion.div> : <motion.div key="idle" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 10
        }} className="flex gap-2" data-id="element-586">
              <button onClick={() => setInputMode('text')} className="flex-1 h-12 flex items-center justify-center gap-2 rounded-full bg-[#1c1c22] border border-white/[0.06] text-[#e8e4df] hover:bg-[#22222a] transition-colors" data-id="element-587">
                <Pencil size={14} strokeWidth={1.8} data-id="element-588" />
                <span className="text-[13px]" data-id="element-589">Write</span>
              </button>
              <button onClick={() => setInputMode('voice')} className="flex-1 h-12 flex items-center justify-center gap-2 rounded-full bg-[#1c1c22] border border-white/[0.06] text-[#e8e4df] hover:bg-[#22222a] transition-colors" data-id="element-590">
                <Mic size={14} strokeWidth={1.8} data-id="element-591" />
                <span className="text-[13px]" data-id="element-592">Speak</span>
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
function MessageBubble({
  message
}: {
  message: CodeMessage;
}) {
  if (message.role === 'system') {
    return <div className="flex justify-center" data-id="element-593">
        <div className="text-[11px] text-[#8a8680]/70 italic" data-id="element-594">{message.content}</div>
      </div>;
  }
  const isUser = message.role === 'user';
  return <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-id="element-595">
      <div className={`max-w-[85%] ${isUser ? '' : 'w-full'}`} data-id="element-596">
        <div className={`rounded-[16px] px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap ${isUser ? 'bg-[#4a9a9a]/15 text-[#e8e4df]' : 'bg-[#1c1c22] border border-white/[0.04] text-[#e8e4df]'}`} data-id="element-597">
          {renderMarkdown(message.content)}
        </div>
        {message.toolCalls && message.toolCalls.map(tc => <ToolCallBlock key={tc.id} call={tc} data-id="element-598" />)}
        {message.timestamp && <div className={`text-[10px] text-[#8a8680]/50 mt-1 ${isUser ? 'text-right' : ''}`} data-id="element-599">
            {message.timestamp}
          </div>}
      </div>
    </div>;
}
function renderMarkdown(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-[#e8e4df] font-semibold" data-id="element-600">{part.slice(2, -2)}</strong>;
    }
    return <span key={i} data-id="element-601">{part}</span>;
  });
}
function ToolCallBlock({
  call
}: {
  call: ToolCall;
}) {
  const [open, setOpen] = useState(false);
  return <div className="mt-2 bg-black/20 border border-white/[0.04] rounded-[12px] overflow-hidden" data-id="element-602">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.02] transition-colors" data-id="element-603">
        <ChevronRight size={12} className={`text-[#8a8680] transition-transform ${open ? 'rotate-90' : ''}`} strokeWidth={1.8} data-id="element-604" />
        <Wrench size={11} className="text-[#4a9a9a]" strokeWidth={1.8} data-id="element-605" />
        <span className="text-[11px] text-[#4a9a9a] font-mono" data-id="element-606">{call.name}</span>
        <span className="text-[11px] text-[#8a8680]/70 font-mono truncate flex-1 text-left" data-id="element-607">
          {call.input}
        </span>
        {call.output && !open && <span className="text-[10px] text-[#8a8680]" data-id="element-608">{call.output}</span>}
      </button>
      <AnimatePresence data-id="element-609">
        {open && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-610">
            <div className="px-3 pb-3 space-y-2" data-id="element-611">
              {call.output && <div className="text-[11px] text-[#8a8680] font-mono" data-id="element-612">{call.output}</div>}
              {call.diff && <div className="bg-black/30 rounded-[8px] px-3 py-2" data-id="element-613">
                  <div className="flex items-center gap-2 text-[10px] text-[#8a8680] font-mono mb-1" data-id="element-614">
                    <span data-id="element-615">{call.diff.file}</span>
                    <span className="text-[#4a9a9a]" data-id="element-616">+{call.diff.added}</span>
                    <span className="text-[#e8a84c]" data-id="element-617">−{call.diff.removed}</span>
                  </div>
                  {call.diff.snippet && <pre className="text-[11px] text-[#8a8680] font-mono whitespace-pre-wrap" data-id="element-618">
                      {call.diff.snippet}
                    </pre>}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function FinalActionCard({
  question,
  kind,
  choices,
  onPickChoice
}: {
  question: string;
  kind: 'choice' | 'open';
  choices?: FinalActionChoice[];
  onPickChoice: (choice: FinalActionChoice) => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-[#1c1c22] border-l-[3px] border-l-[#e8a84c] border-y border-r border-white/[0.04] rounded-[16px] px-4 py-4" data-id="element-619">
      <div className="text-[11px] text-[#e8a84c] uppercase tracking-wide mb-2" data-id="element-620">Waiting for you</div>
      <p className="text-[14px] text-[#e8e4df] leading-relaxed mb-4" data-id="element-621">{question}</p>
      {kind === 'choice' && choices && <div className="space-y-2" data-id="element-622">
          {choices.map(choice => <button key={choice.key} onClick={() => onPickChoice(choice)} className="w-full flex items-start gap-3 px-3 py-2.5 bg-white/[0.03] hover:bg-[#4a9a9a]/15 border border-transparent hover:border-[#4a9a9a]/30 rounded-[12px] text-left transition-colors" data-id="element-623">
              <span className="text-[12px] text-[#4a9a9a] font-mono mt-0.5 min-w-[24px]" data-id="element-624">{choice.key}</span>
              <span className="text-[13px] text-[#e8e4df] flex-1" data-id="element-625">{choice.label}</span>
            </button>)}
        </div>}
      {kind === 'open' && <div className="text-[12px] text-[#8a8680] italic" data-id="element-626">↓ Reply using the pencil or mic below</div>}
    </motion.div>;
}